import {
  EXTERNAL_NAME_PATTERN,
  parseBucketDate,
} from '@/components/ui/chart/utils/formatters'
import type {
  BarLineChartSpec,
  ChartSpec,
  DataRange,
  DateBounds,
  ExpandedFilters,
  GraphChartSpec,
  GraphLinkSpec,
  GraphNodeSpec,
  ResolvedGraphLink,
} from './types'

function matchesQuery(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.trim().toLowerCase())
}

// Keeps the `limit` items with the largest magnitude while preserving the
// original order (so a bar chart stays chronological after a top-N filter).
function keepLargest<Item>(
  items: Item[],
  magnitudeOf: (item: Item) => number,
  limit: number,
): Item[] {
  if (limit <= 0 || items.length <= limit) return items
  const keptIndices = new Set(
    items
      .map((item, index) => ({ index, magnitude: Math.abs(magnitudeOf(item)) }))
      .sort((first, second) => second.magnitude - first.magnitude)
      .slice(0, limit)
      .map((ranked) => ranked.index),
  )
  return items.filter((_item, index) => keptIndices.has(index))
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function isGraphSpec(spec: ChartSpec): spec is GraphChartSpec {
  return spec.type === 'sankey' || spec.type === 'network'
}

export function isChartEmpty(spec: ChartSpec): boolean {
  if (isGraphSpec(spec)) {
    return spec.nodes.length === 0 || spec.links.length === 0
  }
  return !spec.data || spec.data.length === 0
}

export function computeValueRange(spec: ChartSpec): DataRange {
  const values = collectValues(spec)
  if (values.length === 0) return { min: 0, max: 0 }
  let min = values[0]
  let max = values[0]
  for (const value of values) {
    if (value < min) min = value
    if (value > max) max = value
  }
  return { min, max }
}

function collectValues(spec: ChartSpec): number[] {
  if (isGraphSpec(spec)) return spec.links.map((link) => link.value)
  if (!spec.data) return []
  return spec.data.map((point) => Math.abs(point.value))
}

export function applyExpandedFilters(
  spec: ChartSpec,
  filters: ExpandedFilters,
): ChartSpec {
  if (isGraphSpec(spec)) return applyGraphFilters(spec, filters)
  return applyBarLineFilters(spec, filters)
}

function applyBarLineFilters(
  spec: BarLineChartSpec,
  filters: ExpandedFilters,
): BarLineChartSpec {
  let data = spec.data

  const query = filters.labelQuery.trim()
  if (query) {
    data = data.filter((point) =>
      matchesQuery(point.formattedDate ?? point.date, query),
    )
  }

  if (filters.dateStart || filters.dateEnd) {
    const start = filters.dateStart ? parseBucketDate(filters.dateStart) : null
    const end = filters.dateEnd ? parseBucketDate(filters.dateEnd) : null
    data = data.filter((point) => {
      const date = parseBucketDate(point.date)
      if (!date) return true // not a dated point: the date window doesn't apply
      if (start && date < start) return false
      if (end && date > end) return false
      return true
    })
  }

  if (filters.minValue > 0) {
    data = data.filter((point) => Math.abs(point.value) >= filters.minValue)
  }

  if (filters.topN !== null) {
    data = keepLargest(data, (point) => point.value, filters.topN)
  }

  return data === spec.data ? spec : { ...spec, data }
}

function applyGraphFilters(
  spec: GraphChartSpec,
  filters: ExpandedFilters,
): GraphChartSpec {
  let nodes = spec.nodes
  let links = spec.links

  const query = filters.labelQuery.trim()
  if (query) {
    links = links.filter(
      (link) =>
        matchesQuery(link.source, query) || matchesQuery(link.target, query),
    )
  }

  if (filters.minValue > 0) {
    links = links.filter((link) => link.value >= filters.minValue)
  }

  if (filters.topN !== null && links.length > filters.topN) {
    links = keepLargest(links, (link) => link.value, filters.topN)
  }

  if (spec.type === 'network' && filters.hideExternal) {
    const droppedNames = new Set(
      nodes
        .filter((node) => EXTERNAL_NAME_PATTERN.test(node.name))
        .map((node) => node.name),
    )
    if (droppedNames.size > 0) {
      nodes = nodes.filter((node) => !droppedNames.has(node.name))
      links = links.filter(
        (link) =>
          !droppedNames.has(link.source) && !droppedNames.has(link.target),
      )
    }
  }

  // Drop nodes that no longer participate in any link so the layout
  // doesn't leave orphans floating after filtering.
  if (
    links.length !== spec.links.length ||
    nodes.length !== spec.nodes.length
  ) {
    nodes = nodes.filter(participatesIn(links))
  }

  return { ...spec, nodes, links }
}

function participatesIn(
  links: GraphLinkSpec[],
): (node: GraphNodeSpec) => boolean {
  const active = new Set<string>()
  for (const link of links) {
    active.add(link.source)
    active.add(link.target)
  }
  return (node) => active.has(node.name)
}

// Earliest/latest dates in a bar/line chart, for seeding the date-range inputs.
// Returns null unless every point parses as a date (so the date control only
// shows for genuine time series, not categorical bar charts).
export function computeDateBounds(spec: ChartSpec): DateBounds | null {
  if (isGraphSpec(spec) || !spec.data?.length) return null
  const dates = spec.data
    .map((point) => parseBucketDate(point.date))
    .filter((date): date is Date => date !== null)
  if (dates.length !== spec.data.length) return null
  const times = dates.map((date) => date.getTime())
  return {
    min: toIsoDate(new Date(Math.min(...times))),
    max: toIsoDate(new Date(Math.max(...times))),
  }
}

export function resolveGraphLinks(
  nodes: GraphNodeSpec[],
  links: GraphLinkSpec[],
): ResolvedGraphLink[] {
  const nameToIndex = new Map<string, number>()
  nodes.forEach((node, index) => {
    nameToIndex.set(node.name, index)
  })
  return links
    .map((link) => {
      const sourceIndex = nameToIndex.get(link.source)
      const targetIndex = nameToIndex.get(link.target)
      if (sourceIndex === undefined || targetIndex === undefined) return null
      return { source: sourceIndex, target: targetIndex, value: link.value }
    })
    .filter((link): link is ResolvedGraphLink => link !== null)
}
